<script setup lang="ts">
import {
  ref, computed, reactive, onMounted, nextTick
} from 'vue';
import { storeToRefs, StoreGeneric } from 'pinia';
import { ComboBox } from '@progress/kendo-vue-dropdowns';
import { filterBy } from '@progress/kendo-data-query';
import ConnxField from '@/components/Molecules/Field/ConnxField.vue';
import { InputType } from '@/interfaces/InputFields';
import ConnxButton from '@/components/Atoms/ConnxButton.vue';
import ConnxIcon from '@/components/Atoms/ConnxIcon.vue';
import ConnxAlert from '@/components/Molecules/ConnxAlert.vue';
import useEmployeeManagementActions from '@/stores/Employee/employee-management-actions';
import useApplicationStore from '@/stores/application';
import ConnxProgressSteps from '@/components/Molecules/ConnxProgressSteps.vue';
import Steps from '@/interfaces/Steps';
import { EmployeeResponse, EmployeeBasicDetails } from '@/interfaces/CBW/EmployeeResponse';
import { CompanyResponse } from '@/interfaces/CBW/CompanyResponse';
import formatDate from '@/lib/format-date';
import { EmployeePayDetailsResponse } from '@/interfaces/CBW/EmployeePayDetailsResponse';

const mergeToEmployeeCode = ref<EmployeeBasicDetails>();
const mergeToEmployeeDetails = ref<EmployeeResponse>();
const mergeToEmployeeCompanyDetails = ref<CompanyResponse>();
const mergeToEmployeePayDetails = ref<EmployeePayDetailsResponse>();
const confirmEmployeeMerge = ref('');
const errorMessage = ref('');

interface Props {
  employeeCode: string;
}

const currentStep = ref(1);
const test = ref();
const steps: Steps[] = reactive([
  {
    title: 'Select Transfer To Employee',
  },
  {
    title: 'Confirm Change',
  },
  {
    title: '',
    icon: 'fas fa-check',
  },
]);

const loading = ref(false);
const applicationStore = useApplicationStore();
const employeeManagementActions = useEmployeeManagementActions();
const { employee } = storeToRefs<StoreGeneric>(employeeManagementActions);
const props = defineProps<Props>();
const completed = ref(false);
const emit = defineEmits(['close']);

const items = ref();
const empList = ref();

onMounted(async () => {
  applicationStore.setLoading(true);
  loading.value = true;

  await employeeManagementActions.init(props.employeeCode);
  await employeeManagementActions.fetchInactiveEmployees();

  empList.value = employeeManagementActions.employeeList.filter(emp => {
    return emp.empCode !== props.employeeCode;
  });

  items.value = empList.value;

  await nextTick();
  applicationStore.setLoading(false);
  loading.value = false;
});

const filterData = (filter: any) => filterBy(empList.value, filter);

const filterChange = (event: any) => {
  items.value = filterData(event.filter);
};

const resetEmployeeList = () => {
  items.value = empList.value = employeeManagementActions.employeeList.filter(emp => {
    return emp.empCode !== props.employeeCode;
  });
};

const submit = async () => {
  if (mergeToEmployeeCode.value) {
    // we can use applicationStore.setLoading(true) which will use the connx loader divProcessing or we can ignore it.
    applicationStore.setLoading(true);
    loading.value = true;

    const response = await employeeManagementActions.mergeEmpCodes(mergeToEmployeeCode.value.empCode);

    applicationStore.setLoading(false);
    loading.value = false;

    currentStep.value = 3;

    if (response?.error) {
      // Error
      steps[2].cssOverrides = 'tw-bg-connx-danger-shade-10';
      steps[2].icon = 'far fa-times';
      steps[2].title = 'Error Encountered!';
      steps[2].cssTextOverrides = 'tw-text-connx-danger-shade-15';

      // eslint-disable-next-line prefer-destructuring
      errorMessage.value = response.error;
    } else {
      // Success
      steps[2].title = 'Success!';
      steps[2].cssTextOverrides = 'tw-text-connx-success';
      completed.value = true;
    }
  }
};

const loadEmployeeToMerge = async () => {
  if (mergeToEmployeeCode.value) {
    // we can use applicationStore.setLoading(true) which will use the connx loader divProcessing or we can ignore it.
    applicationStore.setLoading(true);
    loading.value = true;

    mergeToEmployeeDetails.value = await employeeManagementActions.fetchEmployeeDetails(mergeToEmployeeCode.value.empCode);

    mergeToEmployeeCompanyDetails.value = await employeeManagementActions.fetchCompanyDetails(mergeToEmployeeCode.value.companyId);

    mergeToEmployeePayDetails.value = await employeeManagementActions.fetchEmployeePayDetails(mergeToEmployeeCode.value.empCode);

    await nextTick();
    applicationStore.setLoading(false);
    loading.value = false;
  }
};

const decrementStep = () => {
  if (currentStep.value > 0) {
    currentStep.value -= 1;
  }
};

const incrementStep = () => {
  if (currentStep.value === 1) {
    loadEmployeeToMerge();
    currentStep.value = 2;
  } else if (currentStep.value === 2) {
    submit();
  } else if (currentStep.value > 0) {
    currentStep.value += 1;
  }
};

const nextButtonDisabled = computed(() => {
  if (currentStep.value === 1) {
    return !mergeToEmployeeCode.value;
  }
  if (currentStep.value === 2) {
    return !confirmEmployeeMerge.value;
  }

  return false;
});

const formatAddress = (employee: EmployeeResponse) => {
  return [employee.street, employee.suburb, employee.state].filter(i => i && i !== '').join(', ');
}
</script>

<template>
  <div
    v-if="employee && items"
    class="tw-fixed tw-top-0 tw-right-0 tw-bottom-0 tw-left-0 tw-z-1100 tw-backdrop-opacity-10"
  >
    <div class="tw-py-10 tw-w-full tw-flex tw-flex-col tw-min-h-screen">
      <div class="
          tw-bg-connx-white
          tw-rounded-md
          tw-mx-auto
          tw-border
          tw-border-solid
          tw-flex-1
          tw-w-full
          tw-max-w-[760px]
          tw-h-10/12
          tw-shadow-sm
          tw-flex
        ">
        <div class="tw-grow tw-flex tw-flex-col tw-justify-between">
          <header
            class="tw-border-solid tw-border-b tw-border-connx-grey-tint-80 tw-p-8 tw-flex tw-items-center tw-justify-between"
          >
            <h1 class="tw-text-2xl tw-font-semibold tw-text-connx-secondary-header tw-mb-0">
              Employee Data Transfer
            </h1>
            <button
              type="button"
              class="tw-border-none tw-text-connx-grey-tint-20 hover:tw-text-connx-grey-shade-20"
              @click="emit('close')"
            >
              <connx-icon
                name="fal fa-times"
                class="tw-w-3"
              />
            </button>
          </header>
          <main class="
              tw-mx-auto
              tw-w-full
              tw-max-w-5xl
              tw-h-full
              tw-flex
              tw-flex-col
              tw-pt-10
              tw-justify-start
            ">
            <connx-progress-steps
              :current-step="currentStep"
              :steps="steps"
              class="tw-pb-4 tw-shadow"
            />
            <div class="tw-h-[388px] tw-pt-10 tw-px-8 ScrollArea">
              <div
                v-if="employee && items"
                class="tw-h-full"
              >
                <div v-if="currentStep === 1">
                  <div class="tw-flex tw-items-center tw-justify-center tw-flex-wrap">
                    <div class="tw-grid-cols-3 tw-gap-y-4 tw-gap-x-10 tw-mx-auto tw-inline-grid">
                      <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                        Merge:
                      </h6>
                      <p class="tw-mb-0 tw-col-span-2">
                        {{ employee.givenName }} {{ employee.surname }} ({{ employee.empCode }})
                      </p>
                      <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                        To:
                      </h6>
                      <div class="tw-col-span-2">
                        <combo-box
                          v-model="mergeToEmployeeCode"
                          append-to="test"
                          class="tw-w-80 tw-z-1200 tw-text-base"
                          :data-items="items"
                          text-field="labelText"
                          placeholder="Please select an employee"
                          :filterable="true"
                          @filterchange="filterChange($event)"
                          @blur="resetEmployeeList()"
                        ></combo-box>
                        <connx-field
                          @change="resetEmployeeList()"
                          v-model="employeeManagementActions.includeTerminatedEmployees"
                          name="include-terminated"
                          :type="InputType.Checkbox"
                        >
                          Include Terminated Employees
                        </connx-field>
                      </div>
                    </div>
                    <connx-alert
                      type="info"
                      class="tw-mt-4 tw-py-6"
                    >
                      <div class="tw-w-full">
                        <div class="tw-mb-4">
                          This process will transfer employee data from one employee code to another.
                        </div>
                        <div class="tw-mb-4">
                          This feature is used if you want to transfer data from an old employee code to a new employee
                          code, for example a returning employee.
                        </div>
                        <div class="tw-mb-4">
                          Note: During this process, the old employee code will lose all it’s data
                          except for leave, timesheets, employee changes, pay information and employee
                          details.
                        </div>
                        <div>
                          If you need the data to remain on the original employee code, this function should not be
                          used.
                        </div>
                      </div>
                    </connx-alert>
                  </div>
                </div>
                <div
                  v-if="currentStep === 2"
                  class="tw-h-full"
                >
                  <div class="tw-flex-wrap tw-flex tw-items-start
                   tw-h-full">
                    <div class="tw-w-full tw-grid-cols-3 tw-gap-x-10 tw-gap-y-4 tw-mx-auto tw-inline-grid tw-mb-4">
                      <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20 tw-col-start-2">
                        From
                      </h6>
                      <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                        To
                      </h6>
                      <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                        Employee Code:
                      </h6>
                      <p class="tw-mb-0 tw-break-words">
                        {{ employee.empCode }}
                      </p>
                      <p class="tw-mb-0 tw-break-words">
                        {{ mergeToEmployeeDetails?.empCode }}
                      </p>

                      <h6
                        v-if="employee.givenName && mergeToEmployeeDetails && mergeToEmployeeDetails.givenName"
                        class="tw-leading-normal tw-text-connx-grey-shade-20 tw-mb-0"
                      >
                        First Name:
                      </h6>
                      <p
                        v-if="employee.givenName && mergeToEmployeeDetails && mergeToEmployeeDetails.givenName"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ employee.givenName }}
                      </p>
                      <p
                        v-if="employee.givenName && mergeToEmployeeDetails && mergeToEmployeeDetails.givenName"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ mergeToEmployeeDetails?.givenName }}
                      </p>

                      <h6
                        v-if="employee.preferredName && mergeToEmployeeDetails && mergeToEmployeeDetails.preferredName"
                        class="tw-leading-normal tw-text-connx-grey-shade-20 tw-mb-0"
                      >
                        Preferred Name:
                      </h6>
                      <p
                        v-if="employee.preferredName && mergeToEmployeeDetails && mergeToEmployeeDetails.preferredName"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ employee.preferredName ?? '-' }}
                      </p>
                      <p
                        v-if="employee.preferredName && mergeToEmployeeDetails && mergeToEmployeeDetails.preferredName"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ mergeToEmployeeDetails?.preferredName ?? '-' }}
                      </p>

                      <h6
                        v-if="employee.middleNames && mergeToEmployeeDetails && mergeToEmployeeDetails.middleNames"
                        class="tw-leading-normal tw-text-connx-grey-shade-20 tw-mb-0"
                      >
                        Middle Name:
                      </h6>
                      <p
                        v-if="employee.middleNames && mergeToEmployeeDetails && mergeToEmployeeDetails.middleNames"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ employee.middleNames ?? '-' }}
                      </p>
                      <p
                        v-if="employee.middleNames && mergeToEmployeeDetails && mergeToEmployeeDetails.middleNames"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ mergeToEmployeeDetails?.middleNames ?? '-' }}
                      </p>

                      <h6
                        v-if="employee.surname && mergeToEmployeeDetails && mergeToEmployeeDetails.surname"
                        class="tw-leading-normal tw-text-connx-grey-shade-20 tw-mb-0"
                      >
                        Surname:
                      </h6>
                      <p
                        v-if="employee.surname && mergeToEmployeeDetails && mergeToEmployeeDetails.surname"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ employee.surname }}
                      </p>
                      <p
                        v-if="employee.surname && mergeToEmployeeDetails && mergeToEmployeeDetails.surname"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ mergeToEmployeeDetails?.surname }}
                      </p>

                      <h6
                        v-if="employee.birthDate && mergeToEmployeeDetails && mergeToEmployeeDetails.birthDate"
                        class="tw-leading-normal tw-text-connx-grey-shade-20 tw-mb-0"
                      >
                        Date of Birth:
                      </h6>
                      <p
                        v-if="employee.birthDate && mergeToEmployeeDetails && mergeToEmployeeDetails.birthDate"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ (employee.birthDate ? formatDate(employee.birthDate, false,
                          'D MMMM YYYY') : '-') }}
                      </p>
                      <p
                        v-if="employee.birthDate && mergeToEmployeeDetails && mergeToEmployeeDetails.birthDate"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ (mergeToEmployeeDetails?.birthDate ? formatDate(mergeToEmployeeDetails.birthDate, false,
                          'D MMMM YYYY') : '-') }}
                      </p>

                      <h6
                        v-if="formatAddress(employee) !== '' && mergeToEmployeeDetails && formatAddress(mergeToEmployeeDetails) !== ''"
                        class="tw-leading-normal tw-text-connx-grey-shade-20 tw-mb-0"
                      >
                        Residential Address:
                      </h6>
                      <p
                        v-if="formatAddress(employee) !== '' && mergeToEmployeeDetails && formatAddress(mergeToEmployeeDetails) !== ''"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ formatAddress(employee) }}
                      </p>
                      <p
                        v-if="formatAddress(employee) !== '' && mergeToEmployeeDetails && formatAddress(mergeToEmployeeDetails) !== ''"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ formatAddress(mergeToEmployeeDetails) }}
                      </p>

                      <h6
                        v-if="employeeManagementActions.company && employeeManagementActions.company.companyName && mergeToEmployeeCompanyDetails && mergeToEmployeeCompanyDetails.companyName"
                        class="tw-leading-normal tw-text-connx-grey-shade-20 tw-mb-0"
                      >
                        Payroll DB:
                      </h6>
                      <p
                        v-if="employeeManagementActions.company && employeeManagementActions.company.companyName && mergeToEmployeeCompanyDetails && mergeToEmployeeCompanyDetails.companyName"
                        class="tw-leading-normal tw-break-words tw-mb-0"
                      >
                        {{ employeeManagementActions.company?.companyName }} (ID: {{ employee.companyId }})
                      </p>
                      <p
                        v-if="employeeManagementActions.company && employeeManagementActions.company.companyName && mergeToEmployeeCompanyDetails && mergeToEmployeeCompanyDetails.companyName"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ mergeToEmployeeCompanyDetails?.companyName }} (ID: {{ mergeToEmployeeDetails?.companyId }})
                      </p>

                      <h6
                        v-if="employeeManagementActions.employeePayDetails && employeeManagementActions.employeePayDetails.hiredDate && mergeToEmployeePayDetails && mergeToEmployeePayDetails.hiredDate"
                        class="tw-leading-normal tw-text-connx-grey-shade-20 tw-mb-0"
                      >
                        Date Hired:
                      </h6>
                      <p
                        v-if="employeeManagementActions.employeePayDetails && employeeManagementActions.employeePayDetails.hiredDate && mergeToEmployeePayDetails && mergeToEmployeePayDetails.hiredDate"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ (employeeManagementActions.employeePayDetails?.hiredDate
                          ? formatDate(employeeManagementActions.employeePayDetails?.hiredDate, false, 'D MMMM YYYY') :
                          '-') }}
                      </p>
                      <p
                        v-if="employeeManagementActions.employeePayDetails && employeeManagementActions.employeePayDetails.hiredDate && mergeToEmployeePayDetails && mergeToEmployeePayDetails.hiredDate"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ (mergeToEmployeePayDetails?.hiredDate ? formatDate(mergeToEmployeePayDetails?.hiredDate,
                          false, 'D MMMM YYYY') : '-') }}
                      </p>

                      <h6
                        v-if="employee.workEmailAddress && mergeToEmployeeDetails && mergeToEmployeeDetails.workEmailAddress"
                        class="tw-leading-normal tw-text-connx-grey-shade-20 tw-mb-0"
                      >
                        Work Email Address:
                      </h6>
                      <p
                        v-if="employee.workEmailAddress && mergeToEmployeeDetails && mergeToEmployeeDetails.workEmailAddress"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ employee.workEmailAddress ?? '-' }}
                      </p>
                      <p
                        v-if="employee.workEmailAddress && mergeToEmployeeDetails && mergeToEmployeeDetails.workEmailAddress"
                        class="tw-break-words tw-mb-0"
                      >
                        {{ mergeToEmployeeDetails?.workEmailAddress ?? '-' }}
                      </p>
                    </div>
                    <div class="tw-flex tw-flex-wrap tw-justify-center">
                      <connx-alert
                        type="info"
                        class="tw-w-full tw-my-4 tw-py-6"
                      >
                        <div class="tw-w-full">
                          <div class="tw-mb-4">
                            Please check the employee data you are transferring to is accurate in the From and To. In
                            most cases this will be the same employee, for example a re-hire.
                          </div>
                          <div class="tw-mb-0">
                            Note: It is advised to make a backup of the database before proceeding.
                          </div>
                        </div>
                      </connx-alert>
                    </div>
                  </div>
                </div>
                <div v-if="currentStep === 3">
                  <div
                    v-if="errorMessage"
                    class="tw-flex tw-justify-center"
                  >
                    <connx-alert
                      type="error"
                      class="tw-py-6"
                    >
                      <div>
                        <h6 class="tw-mb-0 tw-leading-normal tw-text-connx-grey-shade-20">
                          The employee record of
                          {{ employee.givenName }} {{ employee.surname }} ({{ employee.empCode }}) was not merged.
                        </h6>
                        Error: {{ errorMessage }}
                      </div>
                    </connx-alert>
                  </div>
                  <div
                    v-else
                    class="tw-flex tw-justify-center"
                  >
                    <connx-alert
                      type="success"
                      class="tw-py-6"
                    >
                      Data of {{ employee.givenName }} {{ employee.surname }} ({{ employee.empCode }}) have been
                      transferred to {{ mergeToEmployeeDetails?.givenName }} {{ mergeToEmployeeDetails?.surname }} ({{
                        mergeToEmployeeDetails?.empCode }}) successfully.
                    </connx-alert>
                  </div>
                </div>
              </div>
            </div>
          </main>

          <footer
            class="tw-w-full tw-bg-connx-grey-tint-85 tw-mx-auto tw-border-solid tw-border-t tw-border-connx-grey-tint-80 tw-py-6"
          >
            <div class="tw-px-8">
              <div
                class="tw-flex tw-flex-wrap tw-items-start "
                :class="{
                  'tw-justify-center': currentStep !== 2,
                  'tw-justify-between': currentStep === 2,
                }"
              >
                <connx-field
                  v-if="currentStep === 2"
                  v-model="confirmEmployeeMerge"
                  name="confirm-emp-merge"
                  unpadded
                  class="tw-w-full md:tw-w-auto tw-p-0 tw-py-4 md:tw-py-0 md:tw-pr-4 tw-mb-0"
                  :type="InputType.Checkbox"
                >
                  <div>
                    <p class="tw-mb-1">
                      I understand the changes are permanent and there is no rollback.
                    </p>
                    <p class="tw-mb-0">
                      I still would like to continue with the process.
                    </p>
                  </div>
                </connx-field>
                <div>
                  <connx-button
                    v-if="currentStep > 1 && !completed"
                    icon="fas fa-arrow-left"
                    icon-position="before"
                    aria-label="Previous"
                    class="tw-bg-connx-white tw-w-full md:tw-w-36 md:tw-mr-4 tw-mb-4 md:tw-mb-0"
                    @click="decrementStep()"
                  >
                    Previous
                  </connx-button>
                  <connx-button
                    v-if="!completed"
                    :icon="currentStep === 1 ? 'fas fa-arrow-right' : 'far fa-shield-check'"
                    :icon-position="currentStep === 1 ? 'after' : 'before'"
                    button-style="success"
                    aria-label="Next"
                    :disabled="nextButtonDisabled"
                    @click="incrementStep()"
                  >
                    {{ currentStep === 1 ? 'Next' : 'Complete' }}
                  </connx-button>
                  <connx-button
                    v-if="completed"
                    class="tw-bg-connx-white"
                    @click="emit('close')"
                  >
                    Close
                  </connx-button>
                </div>
              </div>
            </div>
          </footer>
        </div>
      </div>
    </div>
    <div
      id="test"
      ref="test"
    ></div>
  </div>
</template>
